import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { AdminTopline, Panel } from "../../components/console";
import { api, authHeaders, describeError } from "../../lib/api";
import { getCurrentRole } from "../../lib/auth";
import { ROLE_LABEL_LO } from "../../lib/roles";

/**
 * ບໍລິການເສີມ (ADR-0027) — super admin เปิด/ปิดบริการเสริมทั้งสี่ของแอป
 *
 * ค่าอยู่ใน system_settings คีย์ `services.<key>` (ไม่เคยตั้ง = เปิด) และมีผลจริงที่ server — 3 สถานะ:
 *   - ເປີດ (`true`)
 *   - ກຳລັງພັດທະນາ (`"development"`) — การ์ดยังโชว์บนหน้าหลัก กดแล้วหน้าเว็บในแอปบอกว่ากำลังพัฒนา
 *     server ไม่รับเรื่องใหม่ (409 SERVICE_DEVELOPING) — แอปที่ติดตั้งแล้วไม่ต้องอัปเดต (ดู domain.ServiceMode)
 *   - ປິດ (`false`) — API ไม่รับเรื่องใหม่ (409 SERVICE_DISABLED) + หน้าหลักของแอปซ่อนการ์ด
 * เรื่องที่ยื่นไว้แล้ว (โหมดปิด) ยังเดินต่อได้ตามปกติ — ข้อความบนจอต้องบอกตรงนี้ให้ชัด
 */
const SERVICES = [
  {
    key: "residence",
    title: "ແຈ້ງພັກເຊົາ & ຍົກຍ້າຍ",
    detail: "ແຈ້ງພັກເຊົາຊົ່ວຄາວ, ແຈ້ງຄົນເຂົ້າ-ອອກ, ຍົກຍ້າຍສຳມະໂນຄົວ",
  },
  {
    key: "assets",
    title: "ທຸລະກຳຊັບສິນ",
    detail: "ສັນຍາຊື້-ຂາຍທີ່ດິນ ແລະ ພາຫະນະ (ກວດປະຫວັດຊັບສິນຍັງເປີດໃຫ້ກວດໄດ້)",
  },
  {
    key: "consult",
    title: "ປຶກສາ & ນິຕິກຳຄອບຄົວ",
    detail: "ນັດປຶກສາ ໄກ່ເກ່ຍ, ມໍລະດົກ, ຢ່າຮ້າງ",
  },
  {
    key: "report",
    title: "ແຈ້ງເຫດ & ສຽງປະຊາຊົນ",
    detail: "ແຈ້ງເຫດພ້ອມຮູບ ແລະ ພິກັດ GPS",
  },
] as const;

type ServiceKey = (typeof SERVICES)[number]["key"];

const settingKey = (k: ServiceKey) => `services.${k}`;

/** ค่าใน system_settings: true = on, false = off, "development" */
type ServiceMode = "on" | "development" | "off";

const MODES: readonly (readonly [ServiceMode, string])[] = [
  ["on", "ເປີດ"],
  ["development", "ກຳລັງພັດທະນາ"],
  ["off", "ປິດ"],
];

const MODE_NOTE: Record<ServiceMode, string> = {
  on: "ເປີດໃຫ້ປະຊາຊົນໃຊ້ຢູ່",
  development: "ສະແດງໃນແອັບ ແຕ່ກົດແລ້ວຂຶ້ນວ່າກຳລັງພັດທະນາ — ຍັງບໍ່ຮັບເລື່ອງ",
  off: "ເຊື່ອງອອກຈາກແອັບ ແລະ ບໍ່ຮັບເລື່ອງໃໝ່",
};

interface SwitchState {
  mode: ServiceMode;
  updatedAt: string | null;
}

/** ไม่เคยตั้ง/อ่านไม่ออก = on (ตรงกับ server: domain.ServiceModeFromSetting) */
function modeOf(value: unknown): ServiceMode {
  if (value === false) return "off";
  if (value === "development") return "development";
  return "on";
}

async function fetchSwitches(): Promise<Record<ServiceKey, SwitchState>> {
  const { data, error, response } = await api.GET("/v1/admin/system/settings", { headers: authHeaders() });
  if (error || !data) throw new Error(describeError(error, response.status));
  const byKey = new Map(data.items.map((s) => [s.key, s]));
  const out = {} as Record<ServiceKey, SwitchState>;
  for (const s of SERVICES) {
    const row = byKey.get(settingKey(s.key));
    out[s.key] = { mode: modeOf(row?.value), updatedAt: row?.updated_at ?? null };
  }
  return out;
}

async function putSwitch(key: ServiceKey, mode: ServiceMode): Promise<void> {
  const value = mode === "development" ? "development" : mode === "on";
  const { error, response } = await api.PUT("/v1/admin/system/settings", {
    body: { key: settingKey(key), value },
    headers: authHeaders(),
  });
  if (error) throw new Error(describeError(error, response.status));
}

function stamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function ServicesPage() {
  const role = getCurrentRole();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const switches = useQuery({ queryKey: ["system", "service-switches"], queryFn: fetchSwitches });

  const toggle = useMutation({
    mutationFn: ({ key, mode }: { key: ServiceKey; mode: ServiceMode }) => putSwitch(key, mode),
    onMutate: () => setError(null),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["system", "service-switches"] }),
    onError: (e: Error) => setError(e.message),
  });

  function onChange(key: ServiceKey, title: string, mode: ServiceMode) {
    if (mode !== "on" && !window.confirm(`ຕັ້ງ “${title}” ເປັນ ${MODES.find(([m]) => m === mode)?.[1] ?? ""} ບໍ? ປະຊາຊົນຈະຍື່ນເລື່ອງໃໝ່ບໍ່ໄດ້ຈົນກວ່າຈະເປີດຄືນ`))
      return;
    toggle.mutate({ key, mode });
  }

  return (
    <div>
      <AdminTopline
        title="ບໍລິການເສີມ"
        subtitle="ເປີດ / ກຳລັງພັດທະນາ / ປິດ ບໍລິການເສີມໃນແອັບຂອງປະຊາຊົນ — ມີຜົນທັນທີທົ່ວລະບົບ"
        roleLabel={role ? ROLE_LABEL_LO[role] : undefined}
      />

      {switches.error ? (
        <div className="alert-error" style={{ marginBottom: "var(--space-4)" }}>
          ໂຫຼດຂໍ້ມູນບໍ່ສຳເລັດ: {switches.error.message}
        </div>
      ) : null}
      {error ? (
        <div className="alert-error" style={{ marginBottom: "var(--space-4)" }}>
          ບັນທຶກບໍ່ສຳເລັດ: {error}
        </div>
      ) : null}

      <Panel
        title="ສະຖານະບໍລິການ"
        note="ກຳລັງພັດທະນາ = ແອັບຍັງສະແດງບໍລິການ ແຕ່ກົດເຂົ້າໄປຈະຂຶ້ນວ່າກຳລັງພັດທະນາ · ປິດ = ເຊື່ອງອອກຈາກໜ້າຫຼັກ — ທັງສອງແບບບໍ່ຮັບເລື່ອງໃໝ່ (ປິດ: ເລື່ອງທີ່ຍື່ນໄວ້ແລ້ວຍັງດຳເນີນຕໍ່ໄດ້)"
      >
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          {SERVICES.map((s) => {
            const state = switches.data?.[s.key];
            const busy = switches.isPending || toggle.isPending;
            return (
              <div key={s.key} className="svc-mode-row">
                <span style={{ display: "grid", gap: "var(--space-1)", flex: 1 }}>
                  <span className="switch__label">{s.title}</span>
                  <span className="panel__note" style={{ margin: 0 }}>
                    {s.detail}
                    {state ? ` · ${MODE_NOTE[state.mode]}` : ""}
                    {state?.updatedAt ? ` · ແກ້ໄຂລ່າສຸດ ${stamp(state.updatedAt)}` : ""}
                  </span>
                </span>
                <div className="seg" role="radiogroup" aria-label={`ສະຖານະ ${s.title}`}>
                  {MODES.map(([m, label]) => (
                    <button
                      key={m}
                      type="button"
                      role="radio"
                      aria-checked={state?.mode === m}
                      disabled={busy || !state}
                      className={state?.mode === m ? "seg__btn is-active" : "seg__btn"}
                      onClick={() => {
                        if (state && state.mode !== m) onChange(s.key, s.title, m);
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </Panel>
    </div>
  );
}
